<div class="pj-modal">
    <div class="pj-modal__header">
        <h3>{{ (isEdit ? 'project_modal_title_edit' : 'project_modal_title_add') | translate }}</h3>
        <button type="button" class="pj-modal__close" (click)="activeModal.dismiss()" [attr.aria-label]="'admin_action_close' | translate">&times;</button>
    </div>

    <form class="pj-modal__body" (ngSubmit)="save()">
        <!-- One field column, switched by language. Driven by LanguageService,
             so a new language appears here on its own. -->
        <div class="lang-tabs" role="tablist">
            @for (lang of languages; track lang.code) {
                <button type="button" role="tab" [class.active]="activeLang === lang.code"
                    [attr.aria-selected]="activeLang === lang.code" (click)="activeLang = lang.code">
                    {{ lang.label }}
                    @if (!hasCopy(lang.code)) {
                        <span class="dot" [title]="'project_modal_nothing_written' | translate: { language: lang.label }">•</span>
                    }
                </button>
            }
        </div>

        @for (field of fields; track field.key) {
            <label class="field">
                <span>
                    {{ field.label | translate }}
                    @if (field.required && isCanonical) { <em class="req">*</em> }
                </span>
                @if (field.long) {
                    <textarea [name]="activeLang + '-' + field.key" [(ngModel)]="copy[activeLang][field.key]"
                        [rows]="field.rows ?? 3" [placeholder]="placeholderFor(field) | translate"
                        [class.invalid]="invalid(field.key)"></textarea>
                } @else {
                    <input type="text" [name]="activeLang + '-' + field.key"
                        [(ngModel)]="copy[activeLang][field.key]"
                        (ngModelChange)="field.key === 'title' ? onTitleChange() : null"
                        [placeholder]="placeholderFor(field) | translate"
                        autocomplete="off" [class.invalid]="invalid(field.key)" />
                }
                @if (field.hint) {
                    <small class="hint">{{ field.hint | translate }}</small>
                }
                @if (!isCanonical && canonicalValue(field.key)) {
                    <!-- What is being translated, so the tab is usable on its own. -->
                    <small class="source">EN: {{ canonicalValue(field.key) }}</small>
                }
            </label>
        }

        <!-- The page's address. Language-independent on purpose: the title is
             localised, so it cannot be the URL key. -->
        <label class="field">
            <span>{{ 'project_modal_slug' | translate }} <em class="req">*</em></span>
            <input type="text" name="slug" [(ngModel)]="slug" (input)="onSlugInput()"
                placeholder="greek-parliament" autocomplete="off" [class.invalid]="slugInvalid" />
            @if (slugInvalid) {
                <p class="err">
                    {{ 'project_modal_slug_invalid' | translate }}
                </p>
            } @else if (slugChanged) {
                <p class="warn">
                    {{ 'project_modal_slug_changed' | translate: { newSlug: slugify(slug), oldSlug: project?.slug } }}
                </p>
            }
        </label>

        <div class="field-row">
            <label class="field">
                <span>{{ 'field_year' | translate }}</span>
                <input type="text" name="year" [(ngModel)]="year" placeholder="2023" autocomplete="off" />
            </label>
            <label class="field">
                <span>{{ 'project_field_display_order' | translate }}</span>
                <input type="number" name="displayOrder" [(ngModel)]="displayOrder" min="1" placeholder="1"
                    autocomplete="off" />
                <!-- Says what the number will do: a taken position inserts here
                     and shifts the rest down, the same as dragging the row. -->
                @if (orderNote) {
                    <small class="hint order-note">{{ orderNote }}</small>
                }
            </label>
        </div>

        <div class="field">
            <span>
                {{ 'field_photo' | translate }}
                @if (!isEdit) { <em class="req">*</em> }
            </span>
            <!-- Click or drop. Previewed locally and uploaded to S3 by the
                 server when the form is saved. -->
            <div class="dropzone" [class.dragging]="dragging" [class.has-image]="!!previewUrl"
                [class.invalid]="imageMissing" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)"
                (drop)="onDrop($event)" (click)="fileInput.click()">
                @if (previewUrl) {
                    <img [src]="previewUrl" alt="" class="preview" />
                    <div class="dropzone__meta">
                        <span class="fname">{{ imageName || ('upload_current_photo' | translate) }}</span>
                        @if (newImageFile) {
                            <button type="button" class="link-btn danger"
                                (click)="clearPicked(); $event.stopPropagation()">{{ 'project_modal_undo' | translate }}</button>
                        }
                    </div>
                } @else {
                    <p class="dropzone__hint">
                        <strong>{{ 'upload_drop_title' | translate }}</strong> {{ 'upload_drop_rest' | translate }}<br />
                        {{ 'upload_drop_formats' | translate }}
                    </p>
                }
            </div>
            <input #fileInput type="file" class="file-input" accept="image/png,image/jpeg,image/webp,image/gif"
                (change)="onFilePicked($event)" />
            @if (imageError) {
                <p class="err">{{ imageError }}</p>
            }
            @if (imageMissing) {
                <p class="err">{{ 'project_modal_image_required' | translate }}</p>
            }
        </div>

        <button type="submit" class="pj-modal__submit">{{ (isEdit ? 'admin_action_save_changes' : 'admin_action_create') | translate }}</button>
    </form>
</div>
